/**
 * Sistema de Logging Profesional para AllTuc
 * Reemplaza console.log con logging estructurado
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'fatal';

interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: string;
  context?: Record<string, unknown>;
  error?: Error;
  userId?: string;
}

interface LoggerConfig {
  minLevel: LogLevel;
  enableConsole: boolean;
  enableRemote: boolean;
  enableLocalStorage: boolean;
  maxLocalLogs: number;
}

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
  fatal: 4
};

const DEFAULT_CONFIG: LoggerConfig = {
  minLevel: (import.meta as any).env?.DEV ? 'debug' : 'warn',
  enableConsole: true,
  enableRemote: false,
  enableLocalStorage: (import.meta as any).env?.DEV,
  maxLocalLogs: 1000
};

class Logger {
  private config: LoggerConfig;
  private logQueue: LogEntry[] = [];
  private isLogging = false; // Prevent recursive logging

  constructor(config: Partial<LoggerConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  private shouldLog(level: LogLevel): boolean {
    return LOG_LEVELS[level] >= LOG_LEVELS[this.config.minLevel];
  }

  private formatMessage(entry: LogEntry): string {
    const timestamp = new Date(entry.timestamp).toISOString();
    const context = entry.context ? ` | ${JSON.stringify(entry.context)}` : '';
    const user = entry.userId ? ` [${entry.userId}]` : '';
    return `[${timestamp}] ${entry.level.toUpperCase()}${user}: ${entry.message}${context}`;
  }

  private async sendToRemote(entry: LogEntry): Promise<void> {
    // TODO: Implementar envío a servicio de logging remoto
    if (this.config.enableRemote) {
      // Placeholder para futura integración
      try {
        // await fetch('/api/logs', { method: 'POST', body: JSON.stringify(entry) });
      } catch (e) {
        console.error('[Logger] Failed to send to remote:', e);
      }
    }
  }

  private saveToLocalStorage(entry: LogEntry): void {
    if (!this.config.enableLocalStorage || typeof window === 'undefined') return;

    try {
      const key = 'alltuc_logs';
      const existing = JSON.parse(localStorage.getItem(key) || '[]');
      existing.push(entry);
      
      // Mantener solo los últimos N logs
      if (existing.length > this.config.maxLocalLogs) {
        existing.splice(0, existing.length - this.config.maxLocalLogs);
      }
      
      localStorage.setItem(key, JSON.stringify(existing));
    } catch (e) {
      console.warn('[Logger] Failed to save to localStorage:', e);
    }
  }

  private log(level: LogLevel, message: string, context?: Record<string, unknown>, error?: Error, userId?: string): void {
    if (!this.shouldLog(level)) return;

    const entry: LogEntry = {
      level,
      message,
      timestamp: new Date().toISOString(),
      context,
      error,
      userId
    };

// Console output - skip if we're in a recursive call
    if (this.config.enableConsole && !this.isLogging) {
      this.isLogging = true;
      const formatted = this.formatMessage(entry);

      switch (level) {
        case 'debug':
          console.debug(formatted);
          break;
        case 'info':
          console.info(formatted);
          break;
        case 'warn':
          console.warn(formatted);
          if (error) console.warn(error);
          break;
        case 'error':
        case 'fatal':
          console.error(formatted);
          if (error) console.error(error);
          break;
      }
      this.isLogging = false;
    }

    // Guardar localmente
    this.saveToLocalStorage(entry);

    // Enviar a servicio remoto si está habilitado
    this.sendToRemote(entry);

    // Agregar a cola
    this.logQueue.push(entry);
  }

  // Métodos públicos
  debug(message: string, context?: Record<string, unknown>, userId?: string): void {
    this.log('debug', message, context, undefined, userId);
  }

  info(message: string, context?: Record<string, unknown>, userId?: string): void {
    this.log('info', message, context, undefined, userId);
  }

  warn(message: string, context?: Record<string, unknown>, error?: Error, userId?: string): void {
    this.log('warn', message, context, error, userId);
  }

  error(message: string, errorOrContext?: Error | Record<string, unknown>, contextOrError?: Error | Record<string, unknown>, userId?: string): void {
    // Handle: error(message, Error)
    // Handle: error(message, { key: value })
    // Handle: error(message, Error, { key: value })
    let error: Error | undefined;
    let context: Record<string, unknown> | undefined;

    if (errorOrContext instanceof Error) {
      error = errorOrContext;
      if (contextOrError && !(contextOrError instanceof Error)) {
        context = contextOrError;
      }
    } else if (errorOrContext && typeof errorOrContext === 'object') {
      context = errorOrContext as Record<string, unknown>;
    }

    this.log('error', message, context, error, userId);
  }

  fatal(message: string, errorOrContext?: Error | Record<string, unknown>, contextOrError?: Error | Record<string, unknown>, userId?: string): void {
    let error: Error | undefined;
    let context: Record<string, unknown> | undefined;

    if (errorOrContext instanceof Error) {
      error = errorOrContext;
      if (contextOrError && !(contextOrError instanceof Error)) {
        context = contextOrError;
      }
    } else if (errorOrContext && typeof errorOrContext === 'object') {
      context = errorOrContext as Record<string, unknown>;
    }

    this.log('fatal', message, context, error, userId);
    // En producción, podríamos enviar alertas críticas
  }

  // Utilidades
  getLogs(): LogEntry[] {
    return [...this.logQueue];
  }

  getLogsFromLocalStorage(): LogEntry[] {
    if (typeof window === 'undefined') return [];
    try {
      return JSON.parse(localStorage.getItem('alltuc_logs') || '[]');
    } catch {
      return [];
    }
  }

  clearLocalLogs(): void {
    if (typeof window === 'undefined') return;
    localStorage.removeItem('alltuc_logs');
  }

  setUserId(userId: string): void {
    // Configurar ID de usuario para tracking
    this.config = { ...this.config };
  }
}

// Instancia singleton
export const logger = new Logger();

// Utilidad para reemplazar console.log existentes
export function replaceConsoleLogs(): void {
  if ((import.meta as any).env?.PROD) {
    // En producción, silenciar console.*
    const noop = () => {};
    console.log = noop;
    console.debug = noop;
    console.info = noop;
    
    // Mantener warn y error pero redirigir al logger
    const originalWarn = console.warn;
    console.warn = (...args: unknown[]) => {
      logger.warn(args.join(' '));
      originalWarn.apply(console, args);
    };
    
    const originalError = console.error;
    console.error = (...args: unknown[]) => {
      const message = args.find(a => typeof a === 'string') || 'Error';
      const error = args.find(a => a instanceof Error) as Error | undefined;
      logger.error(message as string, error || new Error(String(message)));
      originalError.apply(console, args);
    };
  }
}

// Hook para React
export function useLogger() {
  return logger;
}